<template>
  <div class="space-y-4 p-4">
    <p class="text-xs leading-relaxed text-[#1c4848]/60">Post verses or hadiths from this page to the feed. Saved blocks only.</p>
    <div class="flex gap-2" role="group" aria-label="What to post">
      <button v-for="option in OPTIONS" :key="option.type" type="button" class="min-h-9 flex-1 rounded-lg border text-xs font-bold" :class="target === option.type ? 'border-[#1c4848] bg-[#1c4848] text-white' : 'border-[#1c4848]/10 text-[#1c4848]/60 hover:bg-[#1c4848]/5'" @click="target = option.type; selected = []">{{ option.label }}</button>
    </div>
    <p v-if="!candidates.length" class="text-sm text-[#1c4848]/50">No {{ target === 'VERSE' ? 'verses' : 'hadiths' }} on this page yet.</p>
    <ul v-else class="space-y-2">
      <li v-for="block in candidates" :key="block.row">
        <label class="flex cursor-pointer items-start gap-2 rounded-xl border border-[#1c4848]/10 bg-white p-3 hover:bg-[#1c4848]/[0.03]">
          <input v-model="selected" type="checkbox" :value="block.row" class="mt-0.5 h-4 w-4 accent-[#1c4848]">
          <span class="line-clamp-3 min-w-0 text-xs leading-snug text-[#1c4848]">{{ block.text }}</span>
        </label>
      </li>
    </ul>
    <p v-if="error" role="alert" class="text-xs text-red-700">{{ error }}</p>
    <p v-if="done" role="status" class="text-xs font-semibold text-emerald-700">Posted to the feed.</p>
    <button type="button" class="min-h-11 w-full rounded-xl bg-[#1c4848] text-xs font-bold text-white disabled:opacity-40" :disabled="!selected.length || busy" @click="post">{{ busy ? 'Posting…' : `Post ${selected.length || ''}`.trim() }}</button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useContentFeedStore } from '@/store/content-feed';
import { usePageContentsStore } from '@/store/page-contents/index.js';

const OPTIONS = [{ type: 'VERSE', label: 'Verses' }, { type: 'HADITH', label: 'Hadiths' }];
const MONTHS = ['januar', 'februar', 'mart', 'april', 'maj', 'juni', 'juli', 'august', 'septembar', 'oktobar', 'novembar', 'decembar'];

const props = defineProps({
  document: { type: Object, default: null },
  rows: { type: Object, default: () => ({}) },
  languageId: { type: Number, default: 1 },
});

const feeds = useContentFeedStore();
const contents = usePageContentsStore();
const target = ref('VERSE');
const selected = ref([]);
const busy = ref(false);
const error = ref('');
const done = ref(false);

const candidates = computed(() => (props.document?.content ?? [])
  .filter((node) => node.type === (target.value === 'VERSE' ? 'verse' : 'hadith') && props.rows[node.attrs?.id])
  .map((node) => ({ row: props.rows[node.attrs.id], text: node.attrs.translation || node.attrs.text || node.attrs.arabic })));

function title() {
  const now = new Date();
  const date = `${now.getDate()}. ${MONTHS[now.getMonth()]}`;
  return target.value === 'HADITH' ? `Hadisi - ${date}` : `Dnevni ajeti - ${date}`;
}

async function post() {
  busy.value = true;
  error.value = '';
  done.value = false;
  try {
    const feed = await feeds.createContentFeed({ title: title(), type: target.value, language_id: props.languageId });
    for (const row of selected.value) await contents.attachContentFeed(row, feed.id);
    await feeds.fetchContentFeeds();
    selected.value = [];
    done.value = true;
  } catch {
    error.value = 'Posting failed. Check the feed before trying again.';
  } finally {
    busy.value = false;
  }
}
</script>
